import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChartBar, FaSearch, FaArrowUp } from 'react-icons/fa';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleUp = keyframes`
  from {
    opacity: 0;
    transform: scaleY(0);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const searchAppear = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ChartCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 1.5rem;
  width: 100%;
  height: auto;
  position: relative;
  box-shadow: 0 4px 6px -1px rgb(0, 0, 0, 0.1);
  transform: scale(0.9);

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 300px;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 16px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px -1px rgb(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  animation: ${searchAppear} 0.5s ease-out forwards;
  position: relative;
  overflow: hidden;
  opacity: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px -2px rgb(99, 102, 241, 0.15);
    background: #ffffff;
  }

  svg {
    color: #6366f1;
    margin-right: 1rem;
    font-size: 1.25rem;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  span {
    color: #4b5563;
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
    border-radius: 12px;
    
    svg {
      font-size: 1.1rem;
      margin-right: 0.75rem;
    }

    span {
      font-size: 0.875rem;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  @media (max-width: 480px) {
    span {
      max-width: 200px; // Prevent text from getting too long
    }
  }
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.5rem;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.6s ease-out forwards;
  animation-delay: 0.2s;
`;

const BarContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 200px;
  padding: 1.5rem 1rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    height: 150px;
    padding: 0.75rem;
  }
`;

const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  flex: 1;
  max-width: 100px;
`;

const Bar = styled.div`
  width: 4px;
  background: #6366f1;
  border-radius: 8px;
  position: relative;
  min-height: 1px;
  animation: ${scaleUp} 0.5s ease-out forwards;
  animation-delay: ${props => props.index * 0.1}s;
  transform-origin: bottom;
  opacity: 0;
  height: ${props => props.height}px;
  margin: 0 auto;
  
  &::after {
    content: '$${props => props.value}';
    position: absolute;
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.875rem;
    font-weight: 600;
    color: #4f46e5;
    white-space: nowrap;
  }
`;

const MonthLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${props => props.index * 0.1 + 0.2}s;
  opacity: 0;
`;

const InsightCard = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0, 0, 0, 0.05);
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: 0.8s;

  svg {
    color: #6366f1;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  span {
    color: #1f2937;
    font-size: 1rem;
    line-height: 1.6;
  }

  strong {
    color: #4f46e5;
    font-weight: 600;
  }
`;

const GrowthIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #059669;
  font-weight: 600;
  font-size: 0.875rem;
  
  svg {
    font-size: 0.75rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: 0.3s;
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0, 0, 0, 0.05);
  text-align: center;

  .value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .label {
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ChartDemo = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const componentRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const monthlyData = [
    { month: 'Jan', revenue: 12500, orders: 156 },
    { month: 'Feb', revenue: 15800, orders: 182 },
    { month: 'Mar', revenue: 19200, orders: 215 }
  ];

  const growthPercentage = ((19200 - 12500) / 12500 * 100).toFixed(1);
  const orderGrowth = ((215 - 156) / 156 * 100).toFixed(1);

  return (
    <Container>
      <DotLottieReact
        src="https://lottie.host/4fa6b73c-3cc9-4c4c-9e5e-1a9ab9c513e9/E6R06YRKig.lottie"
        loop
        autoplay
      />
    </Container>
  );
};

export default ChartDemo;